import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Loader2, Lock, AlertTriangle, Inbox } from "lucide-react";
import { useAuth } from "../lib/auth";
import { ACCESS_LABEL } from "../lib/api";

const AVATAR_TONES = ["bg-celadon-light text-[#2f5a4a]", "bg-ochre-light text-ochre-dark", "bg-[#E4E7EF] text-ink", "bg-sand text-ink"];

export const Initials = ({ name = "?", size = "h-12 w-12 text-base", testid }) => {
  const ini = name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const tone = AVATAR_TONES[(name.charCodeAt(0) + name.length) % AVATAR_TONES.length];
  return (
    <span data-testid={testid} aria-hidden="true" className={`inline-flex shrink-0 items-center justify-center rounded-full font-serif ${size} ${tone}`}>
      {ini}
    </span>
  );
};

export const Spinner = ({ label = "Loading…" }) => (
  <div role="status" data-testid="loading-state" className="flex items-center justify-center gap-2 py-16 text-sm text-[#5a5f69]">
    <Loader2 className="h-5 w-5 animate-spin text-ochre-dark" aria-hidden="true" /> {label}
  </div>
);

export const ErrorBox = ({ message, onRetry }) => (
  <div role="alert" data-testid="error-state" className="flex flex-col items-start gap-3 rounded-2xl border border-[#f1c9c4] bg-[#fdf2f1] p-5 text-sm text-[#7a1f16]">
    <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" aria-hidden="true" /> {message}</div>
    {onRetry && <button className="btn-ghost btn-sm" onClick={onRetry} data-testid="error-retry-btn">Try again</button>}
  </div>
);

export const Empty = ({ title, text, action }) => (
  <div data-testid="empty-state" className="flex flex-col items-center rounded-2xl border border-dashed border-line bg-white/60 px-6 py-14 text-center">
    <Inbox className="mb-3 h-8 w-8 text-celadon-dark" aria-hidden="true" />
    <p className="font-serif text-xl text-ink">{title}</p>
    {text && <p className="mt-1 max-w-md text-sm text-[#5a5f69]">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const AccessBadge = ({ level, locked }) =>
  locked ? (
    <span className="chip-ochre" data-testid="lock-badge"><Lock className="h-3 w-3" aria-hidden="true" /> {ACCESS_LABEL[level] || "Members"} only</span>
  ) : (
    <span className="chip-celadon">{level === "free" || level === "public" ? "Free" : "Unlocked"}</span>
  );

export const PageHead = ({ eyebrow, title, intro, children }) => (
  <header className="kh-container pb-8 pt-12 sm:pt-16">
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <h1 className="max-w-3xl text-4xl sm:text-5xl lg:text-6xl">{title}</h1>
    {intro && <p className="mt-4 max-w-2xl text-base text-[#4a4f59] md:text-lg">{intro}</p>}
    {children}
  </header>
);

export function RequireAuth({ children, admin = false }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

export const JoinCta = ({ text = "Join the Guild to unlock this." }) => (
  <div className="rounded-2xl bg-ink p-6 text-paper" data-testid="join-cta">
    <p className="font-serif text-2xl text-paper">{text}</p>
    <p className="mt-1 text-sm text-paper/80">Members get the full library, members-only events, studio circles and staff-reviewed introductions.</p>
    <div className="mt-4 flex flex-wrap gap-3">
      <Link to="/membership" className="btn-ochre" data-testid="join-cta-membership-link">See membership</Link>
      <Link to="/login" className="btn border border-paper/30 text-paper hover:bg-white/10" data-testid="join-cta-login-link">Log in</Link>
    </div>
  </div>
);

export const Field = ({ label, id, children, hint }) => (
  <div>
    <label htmlFor={id} className="kh-label">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-[#5a5f69]">{hint}</p>}
  </div>
);

export function ConfirmButton({ label, confirmLabel = "Yes, confirm", onConfirm, className = "btn-ghost btn-sm", testid, prompt = "Are you sure?" }) {
  const [ask, setAsk] = useState(false);
  if (!ask) return <button type="button" className={className} onClick={() => setAsk(true)} data-testid={testid}>{label}</button>;
  return (
    <span className="inline-flex flex-wrap items-center gap-2 rounded-full bg-sand px-3 py-1 text-xs" role="group">
      <span>{prompt}</span>
      <button type="button" className="btn-danger btn-sm" onClick={async () => { setAsk(false); await onConfirm(); }} data-testid={`${testid}-confirm`}>{confirmLabel}</button>
      <button type="button" className="btn-ghost btn-sm" onClick={() => setAsk(false)} data-testid={`${testid}-dismiss`}>Keep</button>
    </span>
  );
}
